/*
  Cubitt: "Ábaco embrujado", el juego de Halloween de la home (diseño de marketing, oct. 2026).
  La sección pinta un banner ligero; el juego se monta al tocar "Entra y juega", a pantalla completa encima de la home,
  para que no atrape el scroll ni las flechas del teclado de la página. Dos tableros como en el diseño: horizontal
  (escritorio) y vertical con cruceta (móvil), elegido al abrir según la pantalla. Textos, productos, cupón y niveles
  para ganarlo vienen de los settings de la sección (JSON en [data-hw-config]).
  Modo fácil (por defecto, feedback de marketing): solo 2 fantasmas, más lentos, farol más grande y susto más largo;
  se gana al atrapar los 4 productos ("winMode": "products") o al limpiar N niveles ("levels").
*/

const BAT_PATH =
  'M100 44c-2-6-5-9-8-7l-3-8-1 12C74 30 52 20 14 26c16 6 22 18 18 34 12-9 26-8 34 4 8-11 22-11 28 0 3-3 4-4 6-4s3 1 6 4c6-11 20-11 28 0 8-12 22-13 34-4-4-16 2-28 18-34-38-6-60 4-74 15l-1-12-3 8c-3-2-6 1-8 7z';

const LOGO_SVG = `<svg viewBox="0 0 100 100" aria-hidden="true">
  <g stroke="#EDEDED" stroke-width="4"><line x1="14" y1="30" x2="30" y2="30"/><line x1="14" y1="52" x2="30" y2="52"/><line x1="34" y1="74" x2="54" y2="74"/></g>
  <g fill="#EDEDED">
    <circle class="b" data-i="0" cx="41" cy="30" r="10.5"/><circle class="b" data-i="1" cx="62" cy="30" r="10.5"/><circle class="b" data-i="2" cx="83" cy="30" r="10.5"/>
    <circle class="b" data-i="3" cx="41" cy="52" r="10.5"/><circle class="b" data-i="4" cx="62" cy="52" r="10.5"/><circle class="b" data-i="5" cx="83" cy="52" r="10.5"/>
    <circle class="b" data-i="6" cx="62" cy="74" r="10.5"/><circle class="b" data-i="7" cx="83" cy="74" r="10.5"/><circle class="b" data-i="8" cx="24" cy="74" r="10.5"/>
  </g>
</svg>`;

// Tableros del diseño. "o" = producto (el orden de lectura da el producto 1, 2, 3 y 4), "P" = salida del jugador,
// la casa de los fantasmas (el logo) ocupa 5×5 casillas centradas en la columna hc.
const LAYOUTS = {
  desktop: {
    fw: 2800,
    fh: 1800,
    hc: 14,
    bats: [
      [40, 110, 400, -12],
      [2380, 1300, 380, -8],
    ],
    map: [
      '#############################',
      '#.............#.............#',
      '#o###.#####.#.#.#.#####.###o#',
      '#...........................#',
      '#.###.#.####.###.####.#.###.#',
      '#.....#...............#.....#',
      '#####.#.###.##-##.###.#.#####',
      '#####.#.###.#GGG#.###.#.#####',
      '......#.....#GGG#.....#......',
      '#####.#.###.#GGG#.###.#.#####',
      '#####.#.###.#####.###.#.#####',
      '#.....#...............#.....#',
      '#.###.#.####.###.####.#.###.#',
      '#o..#.........P.........#..o#',
      '###.#.#####.#.#.#.#####.#.###',
      '#...........................#',
      '#############################',
    ],
  },
  mobile: {
    fw: 1000,
    fh: 1500,
    hc: 10,
    bats: [],
    map: [
      '#####################',
      '#.........#.........#',
      '#o###.###.#.###.###o#',
      '#...................#',
      '#.###.#.#####.#.###.#',
      '#.....#.......#.....#',
      '#####.#.##-##.#.#####',
      '#####.#.#GGG#.#.#####',
      '......#.#GGG#.#......',
      '#####.#.#GGG#.#.#####',
      '#####.#.#####.#.#####',
      '#####.#.......#.#####',
      '#####.#.#####.#.#####',
      '#.........#.........#',
      '#.###.###.#.###.###.#',
      '#o..#.....P.....#..o#',
      '###.#.#.#####.#.#.###',
      '#.....#...#...#.....#',
      '#.#######.#.#######.#',
      '#...................#',
      '#####################',
    ],
  },
};

const esc = (value) =>
  String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const isTouch = () => window.matchMedia('(pointer: coarse)').matches;

const pickLayout = () => (window.innerWidth >= 900 && window.innerWidth / window.innerHeight >= 1.15 ? 'desktop' : 'mobile');

function frameHtml(layoutName, cfg) {
  const L = LAYOUTS[layoutName];
  const t = cfg.text;
  const levels = String(cfg.levelsToWin);
  const bats = L.bats
    .map(
      ([left, top, width, rot], i) =>
        `<svg class="cubitt-hw-bat" viewBox="0 0 200 110" style="left:${left}px;top:${top}px;width:${width}px;transform:rotate(${rot}deg);animation-delay:-${(i * 1.3).toFixed(1)}s" aria-hidden="true"><path d="${BAT_PATH}"/></svg>`
    )
    .join('');
  const prod = (i) => {
    const p = cfg.products[i];
    if (!p) return '';
    return `<figure class="cubitt-hw-prod" data-hw-prod="${i}"><img src="${esc(p.image)}" alt="${esc(p.name)}" width="${p.width || ''}" height="${p.height || ''}"><figcaption>${esc(p.name)}<em>· ${esc(t.caught)}</em></figcaption></figure>`;
  };
  const hud = `<div class="cubitt-hw-hud" aria-live="polite">
      <div><small>${esc(t.score)}</small><b data-hw="score" class="is-hot">0</b></div>
      <div><small>${esc(t.best)}</small><b data-hw="best">0</b></div>
      ${cfg.winMode === 'levels' ? `<div><small>${esc(t.level)}</small><b data-hw="level">1</b></div>` : ''}
      <div><small>${esc(t.lives)}</small><span class="cubitt-hw-lives" data-hw="lives"></span></div>
    </div>`;
  const code = cfg.coupon
    ? `<div class="cubitt-hw-code"><span data-hw="code">${esc(cfg.coupon)}</span><button type="button" class="cubitt-hw-ghost" data-hw="copy">${esc(t.copy)}</button></div>
       <a class="cubitt-hw-btn" data-hw="use" href="${esc(cfg.couponUrl)}">${esc(t.use)}</a>
       ${t.winHint ? `<p class="cubitt-hw-win-hint">${esc(t.winHint)}</p>` : ''}`
    : '';
  const stage = `<div class="cubitt-hw-stage">
      <canvas data-hw="canvas" aria-label="${esc(t.board)}"></canvas>
      <div class="cubitt-hw-overlay" data-hw="overlay">
        <h2 data-hw="ovTitle">${esc(t.introTitle)}</h2>
        <div class="cubitt-hw-big" data-hw="ovScore" hidden>0</div>
        <p data-hw="ovText">${esc(t.introText.replace('[niveles]', levels))}</p>
        <div class="cubitt-hw-legend">
          <span><i class="cubitt-hw-dot" style="background:#EDEDED"></i>${esc(t.legendBead)}</span>
          <span><img src="${esc(cfg.products[0] ? cfg.products[0].image : '')}" alt="">${esc(t.legendProduct)}</span>
          <span><i class="cubitt-hw-dot" style="background:#282828;box-shadow:inset 0 0 0 2px #EDEDED"></i>${esc(t.legendGhost)}</span>
        </div>
        <button type="button" class="cubitt-hw-btn" data-hw="start">${esc(t.play)}</button>
      </div>
      <div class="cubitt-hw-overlay cubitt-hw-overlay--win" data-hw="win" hidden>
        <h2>${esc(t.winTitle)}</h2>
        <p>${esc(t.winText.replace('[niveles]', levels))}</p>
        ${code}
        <button type="button" class="cubitt-hw-ghost" data-hw="keep">${esc(t.keepPlaying)}</button>
      </div>
    </div>`;
  const logo = `<div class="cubitt-hw-logo">${LOGO_SVG}</div>`;
  const pause = `<button type="button" class="cubitt-hw-ghost" data-hw="pause">${esc(t.pause)}</button>`;
  const sound = `<button type="button" class="cubitt-hw-ghost" data-hw="sound">${esc(t.soundOn)}</button>`;

  if (layoutName === 'desktop') {
    return `<div class="cubitt-hw-frame">${bats}
      <div class="cubitt-hw-wrap">
        ${logo}
        <p class="cubitt-hw-progress" data-hw="progress"></p>
        <div class="cubitt-hw-layout">
          <div class="cubitt-hw-side">${prod(0)}${prod(2)}</div>
          <div class="cubitt-hw-center">
            ${hud}
            ${stage}
            <div class="cubitt-hw-row">
              <p class="cubitt-hw-hint"><kbd>←</kbd><kbd>↑</kbd><kbd>↓</kbd><kbd>→</kbd> ${esc(t.keys)}</p>
              <p class="cubitt-hw-hint" data-hw="swipe" hidden>${esc(t.swipe)}</p>
              <div class="cubitt-hw-btns">${pause}${sound}</div>
            </div>
          </div>
          <div class="cubitt-hw-side">${prod(1)}${prod(3)}</div>
        </div>
      </div>
    </div>`;
  }

  return `<div class="cubitt-hw-frame">${bats}
    <div class="cubitt-hw-wrap">
      <div class="cubitt-hw-topbar">${sound}${logo}${pause}</div>
      <p class="cubitt-hw-progress" data-hw="progress"></p>
      ${hud}
      ${stage}
      ${
        isTouch()
          ? `<div class="cubitt-hw-bottom cubitt-hw-bottom--touch">${prod(0)}${prod(2)}${prod(1)}${prod(3)}</div>
             <p class="cubitt-hw-swipe">${esc(t.swipe)}</p>`
          : `<div class="cubitt-hw-bottom">
        ${prod(0)}${prod(2)}
        <div class="cubitt-hw-pad" aria-label="${esc(t.pad)}">
          <button type="button" class="u" data-d="up" aria-label="↑">▲</button>
          <button type="button" class="l" data-d="left" aria-label="←">◀</button>
          <button type="button" class="d" data-d="down" aria-label="↓">▼</button>
          <button type="button" class="r" data-d="right" aria-label="→">▶</button>
        </div>
        ${prod(1)}${prod(3)}
      </div>`
      }
    </div>
  </div>`;
}

function createGame(mount, cfg, requestClose) {
  const layoutName = pickLayout();
  const L = LAYOUTS[layoutName];
  const t = cfg.text;
  mount.classList.remove('cubitt-hw-game--desktop', 'cubitt-hw-game--mobile');
  mount.classList.add('cubitt-hw-game', `cubitt-hw-game--${layoutName}`);
  mount.innerHTML = frameHtml(layoutName, cfg);

  const $ = (name) => mount.querySelector(`[data-hw="${name}"]`);
  const frameEl = mount.querySelector('.cubitt-hw-frame');
  const wrapEl = mount.querySelector('.cubitt-hw-wrap');
  const cv = $('canvas');
  const ctx = cv.getContext('2d');
  const overlay = $('overlay');
  const winEl = $('win');
  const beads = [...mount.querySelectorAll('.cubitt-hw-logo .b')].sort((a, b) => a.dataset.i - b.dataset.i);

  const MAP = L.map;
  const COLS = MAP[0].length;
  const ROWS = MAP.length;
  const T = 24;
  const W = COLS * T;
  const H = ROWS * T;
  const TUNNEL_ROW = 8;
  const HC = L.hc;
  const inHouse = (x, y) => x >= HC - 2 && x <= HC + 2 && y >= 6 && y <= 10;
  const C = { night: '#0C1014', rod: '#282828', bead: '#1B1E22', beadHi: '#3C3C3C', bone: '#EDEDED', lantern: '#FF470D', ember: '#FF7A45', scared: '#282828' };
  const EASY = cfg.easy !== false;
  const BY_PRODUCTS = cfg.winMode !== 'levels';
  const ALL_GHOSTS = [
    { name: 'hueso', color: '#EDEDED', home: [HC, 5], corner: [COLS - 2, -2], release: 0 },
    { name: 'bruma', color: '#EDEDED', home: [HC, 8], corner: [1, -2], release: 1.5 },
    { name: 'limo', color: '#EDEDED', home: [HC - 1, 8], corner: [COLS - 2, ROWS + 1], release: 5 },
    { name: 'brasa', color: '#EDEDED', home: [HC + 1, 8], corner: [1, ROWS + 1], release: 9 },
  ];
  /* Cubitt: en fácil, N fantasmas lentos (setting; marketing pidió más de uno) que salen escalonados. */
  const EASY_GHOSTS = Math.min(4, Math.max(1, Number(cfg.easyGhosts) || 3));
  const GHOSTS = EASY ? ALL_GHOSTS.slice(0, EASY_GHOSTS).map((g, i) => ({ ...g, release: 1.5 + i * 3 })) : ALL_GHOSTS;
  let caughtProducts = 0;

  // Productos en las casillas "o" (orden de lectura) y salida del jugador en "P".
  const PRODUCTS = {};
  let startCell = [HC, ROWS - 4];
  let powerIndex = 0;
  MAP.forEach((row, y) =>
    [...row].forEach((c, x) => {
      if (c === 'o' && cfg.products.length) {
        const i = powerIndex++ % cfg.products.length;
        PRODUCTS[`${x},${y}`] = { i, name: String(cfg.products[i].name || '').toUpperCase() };
      }
      if (c === 'P') startCell = [x, y];
    })
  );
  const IMG = cfg.products.map((p) => {
    const img = new Image();
    img.src = p.image;
    return img;
  });

  const DIRS = { up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 } };
  const DIR_ORDER = [DIRS.up, DIRS.left, DIRS.down, DIRS.right];

  const dark = document.createElement('canvas');
  const dctx = dark.getContext('2d');
  const wallsCv = document.createElement('canvas');
  const wctx = wallsCv.getContext('2d');

  const grid = MAP.map((r) => r.split(''));
  const cell = (x, y) => (y < 0 || y >= ROWS ? '#' : x < 0 || x >= COLS ? (y === TUNNEL_ROW ? ' ' : '#') : grid[y][x]);
  const isWall = (x, y) => x >= 0 && x < COLS && cell(x, y) === '#';
  const wrap = (x) => (x + COLS) % COLS;

  function fit() {
    const s = Math.min(mount.clientWidth / L.fw, mount.clientHeight / L.fh);
    frameEl.style.transform = `translate(${(mount.clientWidth - L.fw * s) / 2}px,${(mount.clientHeight - L.fh * s) / 2}px) scale(${s})`;
    resize();
  }

  function resize() {
    const rr = cv.getBoundingClientRect();
    const dpr = Math.min(4, Math.max(1, (rr.width * (window.devicePixelRatio || 1)) / W));
    for (const c of [cv, dark, wallsCv]) {
      c.width = W * dpr;
      c.height = H * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    dctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    wctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawWalls();
  }

  function drawWalls() {
    wctx.clearRect(0, 0, W, H);
    wctx.lineCap = 'round';
    wctx.strokeStyle = C.rod;
    wctx.lineWidth = T * 0.14;
    wctx.beginPath();
    for (let y = 0; y < ROWS; y++)
      for (let x = 0; x < COLS; x++)
        if (isWall(x, y) && !inHouse(x, y)) {
          const cx = (x + 0.5) * T;
          const cy = (y + 0.5) * T;
          if (isWall(x + 1, y) && !inHouse(x + 1, y)) {
            wctx.moveTo(cx, cy);
            wctx.lineTo(cx + T, cy);
          }
          if (isWall(x, y + 1) && !inHouse(x, y + 1)) {
            wctx.moveTo(cx, cy);
            wctx.lineTo(cx, cy + T);
          }
        }
    wctx.stroke();
    // Una cuenta en cada nudo de pared, como en el logo.
    for (let y = 0; y < ROWS; y++)
      for (let x = 0; x < COLS; x++)
        if (isWall(x, y) && !inHouse(x, y)) {
          const cx = (x + 0.5) * T;
          const cy = (y + 0.5) * T;
          const g = wctx.createRadialGradient(cx - T * 0.08, cy - T * 0.1, 1, cx, cy, T * 0.32);
          g.addColorStop(0, C.beadHi);
          g.addColorStop(1, C.bead);
          wctx.fillStyle = g;
          wctx.beginPath();
          wctx.arc(cx, cy, T * 0.3, 0, Math.PI * 2);
          wctx.fill();
        }
    // Casa de los fantasmas = el logo.
    const hx = (HC - 2) * T + T * 0.12;
    const hy = 6 * T + T * 0.12;
    const hs = 5 * T - T * 0.24;
    const hr = T * 0.7;
    wctx.strokeStyle = C.rod;
    wctx.lineWidth = T * 0.14;
    wctx.beginPath();
    wctx.moveTo(HC * T, hy);
    wctx.lineTo(hx + hr, hy);
    wctx.arcTo(hx, hy, hx, hy + hr, hr);
    wctx.lineTo(hx, hy + hs - hr);
    wctx.arcTo(hx, hy + hs, hx + hr, hy + hs, hr);
    wctx.lineTo(hx + hs - hr, hy + hs);
    wctx.arcTo(hx + hs, hy + hs, hx + hs, hy + hs - hr, hr);
    wctx.lineTo(hx + hs, hy + hr);
    wctx.arcTo(hx + hs, hy, hx + hs - hr, hy, hr);
    wctx.lineTo((HC + 1) * T, hy);
    wctx.stroke();
    const u = T / 21;
    const ox = (HC + 0.5) * T - 53.5 * u;
    const oy = 8.5 * T - 52 * u;
    const P = (a, b) => [ox + a * u, oy + b * u];
    wctx.strokeStyle = 'rgba(237,237,237,.5)';
    wctx.lineWidth = 4 * u;
    for (const [a, b, c] of [
      [14, 30, 30],
      [14, 52, 30],
      [34, 74, 54],
    ]) {
      wctx.beginPath();
      wctx.moveTo(...P(a, b));
      wctx.lineTo(...P(c, b));
      wctx.stroke();
    }
    for (const [a, b] of [
      [41, 30],
      [62, 30],
      [83, 30],
      [41, 52],
      [62, 52],
      [83, 52],
      [24, 74],
      [62, 74],
      [83, 74],
    ]) {
      const [cx, cy] = P(a, b);
      const g = wctx.createRadialGradient(cx - 2, cy - 3, 1, cx, cy, 10.5 * u);
      g.addColorStop(0, 'rgba(237,237,237,.95)');
      g.addColorStop(1, 'rgba(170,170,170,.55)');
      wctx.fillStyle = g;
      wctx.beginPath();
      wctx.arc(cx, cy, 10 * u, 0, Math.PI * 2);
      wctx.fill();
    }
    wctx.strokeStyle = C.lantern;
    wctx.lineWidth = 3;
    wctx.setLineDash([4, 4]);
    wctx.beginPath();
    wctx.moveTo(HC * T + 3, hy);
    wctx.lineTo((HC + 1) * T - 3, hy);
    wctx.stroke();
    wctx.setLineDash([]);
  }

  // ---------- estado ----------
  let pellets;
  let pelletCount;
  let score = 0;
  let best = 0;
  let lives = 3;
  let level = 1;
  let state = 'menu';
  let stateT = 0;
  let cleared = 0;
  let logoDone = false;
  let won = false;
  let pendingWin = false;
  let player;
  let ghosts;
  let want = null;
  let frightT = 0;
  let modeIdx = 0;
  let modeT = 0;
  let combo = 0;
  let popups = [];
  let muted = false;
  let time = 0;
  let raf = 0;
  let last = 0;
  const MODES = [7, 20, 7, 20, 5, 20, 5, Infinity];
  try {
    best = +localStorage.getItem('cubitt-hw-best') || 0;
  } catch (e) {}

  function resetPellets() {
    mount.querySelectorAll('.cubitt-hw-prod.is-got').forEach((el) => el.classList.remove('is-got'));
    pellets = new Map();
    pelletCount = 0;
    for (let y = 0; y < ROWS; y++)
      for (let x = 0; x < COLS; x++) {
        const c = grid[y][x];
        if (c === '.' || c === 'o') {
          pellets.set(`${x},${y}`, c === 'o' ? 2 : 1);
          pelletCount++;
        }
      }
  }
  function resetActors() {
    player = { x: startCell[0], y: startCell[1], dir: DIRS.left, p: 0, speed: 7.6 + level * 0.15, moving: true, face: DIRS.left };
    want = DIRS.left;
    ghosts = GHOSTS.map((g, i) => ({ ...g, x: g.home[0], y: g.home[1], dir: DIRS.left, p: 0, state: i === 0 ? 'scatter' : 'house', releaseT: g.release, speed: 0 }));
    frightT = 0;
    modeIdx = 0;
    modeT = 0;
    combo = 0;
  }
  function newGame() {
    caughtProducts = 0;
    score = 0;
    lives = 3;
    level = 1;
    cleared = 0;
    logoDone = false;
    won = false;
    pendingWin = false;
    resetPellets();
    resetActors();
    hud();
    setState('intro');
    playIntro(() => {
      setState('ready');
      tone(660, 0.18, 'triangle', 0.06, 990);
    });
  }

  // ---------- progreso del logo ----------
  function updateLogo(popIdx) {
    beads.forEach((b, i) => {
      b.classList.toggle('lit', i < cleared);
      b.classList.toggle('empty', i === 8 && cleared < 9);
      if (i === popIdx) {
        b.classList.remove('pop');
        void b.getBBox();
        b.classList.add('pop');
      }
    });
    $('progress').textContent = wrapEl.classList.contains('is-game') ? t.progress.replace('[n]', Math.min(cleared, 9)) : '';
  }
  function playIntro(done) {
    wrapEl.classList.add('is-game');
    updateLogo();
    const src = beads[8].getBoundingClientRect();
    const cr = cv.getBoundingClientRect();
    const k = cr.width / W;
    const w = src.width;
    const cx = src.left + w / 2;
    const cy = src.top + w / 2;
    const tx = cr.left + (startCell[0] + 0.5) * T * k;
    const ty = cr.top + (startCell[1] + 0.5) * T * k;
    const sc = (0.84 * T * k) / w;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const f = document.createElement('div');
    f.className = 'cubitt-hw-flyer';
    Object.assign(f.style, { left: `${cx - w / 2}px`, top: `${cy - w / 2}px`, width: `${w}px`, height: `${w}px` });
    mount.parentElement.appendChild(f);
    const dx = tx - cx;
    const dy = ty - cy;
    const anim = f.animate(
      [
        { transform: 'translate(0,0) scale(1)', background: '#fff', offset: 0 },
        { transform: 'translate(-10px,0) scale(1)', background: '#fff', offset: 0.15 },
        { transform: 'translate(-12px,-8px) scale(1.1)', background: '#fff', offset: 0.28 },
        { transform: `translate(${dx * 0.55}px,${dy * 0.45}px) scale(${(1 + sc) / 2})`, background: '#FFB08F', offset: 0.7 },
        { transform: `translate(${dx}px,${dy}px) scale(${sc})`, background: '#FF470D', offset: 1 },
      ],
      { duration: reduce ? 300 : 1500, easing: 'cubic-bezier(.55,0,.45,1)', fill: 'forwards' }
    );
    tone(880, 0.12, 'sine', 0.05, 1320);
    anim.onfinish = () => {
      f.remove();
      done();
    };
  }

  function setState(s) {
    state = s;
    stateT = 0;
  }
  function hud() {
    $('score').textContent = score;
    $('best').textContent = Math.max(best, score);
    if ($('level')) $('level').textContent = level;
    const l = $('lives');
    l.innerHTML = '';
    for (let i = 0; i < lives; i++) l.appendChild(document.createElement('i'));
  }

  // ---------- movimiento ----------
  function passable(e, x, y, isGhost) {
    const c = cell(x, y);
    if (c === '#') return false;
    if (c === '-' || c === 'G') return isGhost && (e.state === 'exit' || e.state === 'eaten');
    return true;
  }
  const can = (e, d, isGhost) => passable(e, wrap(e.x + d.x), e.y + d.y, isGhost);
  const pos = (e) => ({ x: e.x + e.dir.x * e.p, y: e.y + e.dir.y * e.p });
  const opposite = (a, b) => a && b && a.x === -b.x && a.y === -b.y && (a.x || a.y);

  function step(e, dt, choose, arrive, isGhost) {
    let d = e.speed * dt;
    let guard = 0;
    while (d > 0 && guard++ < 8) {
      if (e.p === 0) {
        choose(e);
        if (!can(e, e.dir, isGhost)) {
          e.moving = false;
          return;
        }
      }
      e.moving = true;
      const r = Math.min(d, 1 - e.p);
      e.p += r;
      d -= r;
      if (e.p >= 1 - 1e-9) {
        e.x = wrap(e.x + e.dir.x);
        e.y += e.dir.y;
        e.p = 0;
        arrive(e);
      }
    }
  }
  function reverse(e) {
    if (e.p > 0) {
      e.x = wrap(e.x + e.dir.x);
      e.y += e.dir.y;
      e.p = 1 - e.p;
    }
    e.dir = { x: -e.dir.x, y: -e.dir.y };
  }

  function updatePlayer(dt) {
    if (want && opposite(want, player.dir) && player.p > 0) reverse(player);
    step(
      player,
      dt,
      (e) => {
        if (want && can(e, want, false)) e.dir = want;
      },
      eat,
      false
    );
    if (player.moving) player.face = player.dir;
  }
  function eat(e) {
    const k = `${e.x},${e.y}`;
    const v = pellets.get(k);
    if (!v) return;
    pellets.delete(k);
    pelletCount--;
    if (v === 1) {
      score += 10;
      blip();
    } else {
      score += 50;
      combo = 0;
      if (PRODUCTS[k]) {
        caughtProducts++;
        mount.querySelectorAll(`[data-hw-prod="${PRODUCTS[k].i}"]`).forEach((el) => el.classList.add('is-got'));
        popups.push({ x: e.x, y: e.y - 0.6, t: -0.6, txt: PRODUCTS[k].name });
      }
      frightT = EASY ? 10 : Math.max(2.5, 8 - level * 0.7);
      // Cubitt: en modo "productos" se gana al atrapar todos los productos del tablero.
      if (BY_PRODUCTS && !won && caughtProducts >= Object.keys(PRODUCTS).length) {
        won = true;
        pendingWin = true;
        setState('prize');
        tone(523, 0.4, 'triangle', 0.08, 1046);
      }
      for (const g of ghosts)
        if (g.state === 'chase' || g.state === 'scatter') {
          g.state = 'fright';
          reverse(g);
        }
      tone(220, 0.25, 'sawtooth', 0.05, 660);
    }
    hud();
    if (pelletCount === 0) {
      cleared++;
      logoDone = cleared === 9;
      if (logoDone) {
        score += 5000;
        hud();
        popups.push({ x: HC, y: 11, t: -1, txt: '+5000' });
      }
      // Cubitt: premio (cupón) al limpiar los niveles del setting "Niveles para ganar el cupón".
      if (!BY_PRODUCTS && !won && cleared >= cfg.levelsToWin) {
        won = true;
        pendingWin = true;
      }
      updateLogo(cleared <= 9 ? cleared - 1 : -1);
      setState('clear');
      tone(523, 0.4, 'triangle', 0.08, 1046);
    }
  }

  function ghostTarget(g) {
    const pp = player;
    const f = player.face;
    if (g.state === 'exit') return [HC, 5];
    if (g.state === 'eaten') return [HC, 8];
    if (g.state === 'scatter') return g.corner;
    switch (g.name) {
      case 'hueso':
        return [pp.x, pp.y];
      case 'bruma':
        return [pp.x + f.x * 4, pp.y + f.y * 4];
      case 'limo': {
        const a = ghosts[0];
        const tx = pp.x + f.x * 2;
        const ty = pp.y + f.y * 2;
        return [tx * 2 - a.x, ty * 2 - a.y];
      }
      default:
        return (g.x - pp.x) ** 2 + (g.y - pp.y) ** 2 > 64 ? [pp.x, pp.y] : g.corner;
    }
  }
  function chooseGhost(g) {
    let opts = DIR_ORDER.filter((d) => can(g, d, true) && !opposite(d, g.dir));
    if (!opts.length) opts = DIR_ORDER.filter((d) => can(g, d, true));
    if (!opts.length) return;
    if (g.state === 'fright') {
      g.dir = opts[Math.floor(Math.random() * opts.length)];
      return;
    }
    const [tx, ty] = ghostTarget(g);
    let bestD = Infinity;
    let pick = opts[0];
    for (const d of opts) {
      const dd = (g.x + d.x - tx) ** 2 + (g.y + d.y - ty) ** 2;
      if (dd < bestD) {
        bestD = dd;
        pick = d;
      }
    }
    g.dir = pick;
  }
  const currentMode = () => (modeIdx % 2 === 0 ? 'scatter' : 'chase');
  function arriveGhost(g) {
    if (g.state === 'exit' && g.x === HC && g.y === 5) {
      g.state = currentMode();
      g.dir = Math.random() < 0.5 ? DIRS.left : DIRS.right;
    } else if (g.state === 'eaten' && g.x === HC && g.y === 8) {
      g.state = 'exit';
    }
  }

  function updateGhosts(dt) {
    if (frightT > 0) {
      frightT -= dt;
      if (frightT <= 0) for (const g of ghosts) if (g.state === 'fright') g.state = currentMode();
    } else {
      modeT += dt;
      if (modeT > MODES[modeIdx]) {
        modeT = 0;
        modeIdx++;
        for (const g of ghosts)
          if (g.state === 'chase' || g.state === 'scatter') {
            g.state = currentMode();
            reverse(g);
          }
      }
    }
    const base = EASY ? 4.6 + level * 0.2 : 6.6 + level * 0.35;
    for (const g of ghosts) {
      if (g.state === 'house') {
        g.releaseT -= dt;
        if (g.releaseT <= 0) g.state = 'exit';
        continue;
      }
      const inTunnel = g.y === TUNNEL_ROW && (g.x < 5 || g.x > COLS - 6);
      g.speed = g.state === 'eaten' ? 15 : g.state === 'fright' ? (EASY ? 3.4 : 4.4) : g.state === 'exit' ? 4 : inTunnel ? base * 0.55 : base;
      step(g, dt, chooseGhost, arriveGhost, true);
    }
    const p = pos(player);
    for (const g of ghosts) {
      if (g.state === 'house' || g.state === 'eaten' || g.state === 'exit') continue;
      const q = pos(g);
      let dx = Math.abs(p.x - q.x);
      dx = Math.min(dx, COLS - dx);
      if (dx * dx + (p.y - q.y) ** 2 < 0.45) {
        if (g.state === 'fright') {
          const pts = 200 * 2 ** combo;
          combo = Math.min(combo + 1, 3);
          score += pts;
          hud();
          g.state = 'eaten';
          popups.push({ x: q.x, y: q.y, t: 0, txt: pts });
          tone(880, 0.18, 'square', 0.05, 220);
        } else {
          setState('dying');
          tone(440, 0.9, 'triangle', 0.08, 80);
          return;
        }
      }
    }
  }

  // ---------- dibujo ----------
  function draw() {
    ctx.fillStyle = C.night;
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(wallsCv, 0, 0, W, H);

    for (const [k, v] of pellets) {
      const [x, y] = k.split(',').map(Number);
      const cx = (x + 0.5) * T;
      const cy = (y + 0.5) * T;
      if (v === 1) {
        ctx.fillStyle = C.bone;
        ctx.beginPath();
        ctx.arc(cx, cy, T * 0.1, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const pulse = 0.9 + Math.sin(time * 5 + x) * 0.1;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, T * 1.1);
        g.addColorStop(0, 'rgba(255,110,60,.32)');
        g.addColorStop(1, 'rgba(255,71,13,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, T * 1.1 * pulse, 0, Math.PI * 2);
        ctx.fill();
        const prod = PRODUCTS[k];
        const img = prod && IMG[prod.i];
        if (img && img.complete && img.naturalWidth) {
          const box = T * 1.6;
          const sc = Math.min(box / img.naturalWidth, box / img.naturalHeight);
          const w = img.naturalWidth * sc;
          const h = img.naturalHeight * sc;
          const bob = Math.sin(time * 3 + x) * T * 0.06;
          ctx.save();
          ctx.shadowColor = 'rgba(255,120,70,.85)';
          ctx.shadowBlur = 8;
          ctx.drawImage(img, cx - w / 2, cy - h / 2 + bob, w, h);
          ctx.restore();
        } else {
          ctx.fillStyle = C.lantern;
          ctx.beginPath();
          ctx.arc(cx, cy, T * 0.28 * pulse, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    for (const g of ghosts) drawGhostBody(g);
    if (state !== 'intro') drawPlayer();

    // Oscuridad con un farol alrededor del jugador.
    const p = pos(player);
    const px = (p.x + 0.5) * T;
    const py = (p.y + 0.5) * T;
    const powered = frightT > 0;
    const radius =
      (powered ? 7.5 : EASY ? 4.8 : 3.6) * T * (state === 'dying' ? Math.max(0.2, 1 - stateT) : state === 'intro' ? Math.min(1, stateT / 1.5) * 0.9 + 0.1 : 1);
    dctx.globalCompositeOperation = 'source-over';
    dctx.clearRect(0, 0, W, H);
    dctx.fillStyle = 'rgba(12,16,20,.76)';
    dctx.fillRect(0, 0, W, H);
    dctx.globalCompositeOperation = 'destination-out';
    const lg = dctx.createRadialGradient(px, py, T * 0.5, px, py, radius);
    lg.addColorStop(0, 'rgba(0,0,0,1)');
    lg.addColorStop(0.55, 'rgba(0,0,0,.85)');
    lg.addColorStop(1, 'rgba(0,0,0,0)');
    dctx.fillStyle = lg;
    dctx.beginPath();
    dctx.arc(px, py, radius, 0, Math.PI * 2);
    dctx.fill();
    for (const [k, v] of pellets)
      if (v === 2) {
        const [x, y] = k.split(',').map(Number);
        const cx = (x + 0.5) * T;
        const cy = (y + 0.5) * T;
        const pg = dctx.createRadialGradient(cx, cy, T * 0.3, cx, cy, T * 1.4);
        pg.addColorStop(0, 'rgba(0,0,0,.95)');
        pg.addColorStop(1, 'rgba(0,0,0,0)');
        dctx.fillStyle = pg;
        dctx.beginPath();
        dctx.arc(cx, cy, T * 1.4, 0, Math.PI * 2);
        dctx.fill();
      }
    const lx = (HC + 0.5) * T;
    const hg = dctx.createRadialGradient(lx, 8.5 * T, T, lx, 8.5 * T, 3.2 * T);
    hg.addColorStop(0, 'rgba(0,0,0,.55)');
    hg.addColorStop(1, 'rgba(0,0,0,0)');
    dctx.fillStyle = hg;
    dctx.beginPath();
    dctx.arc(lx, 8.5 * T, 3.2 * T, 0, Math.PI * 2);
    dctx.fill();
    if (powered) {
      const f = player.face;
      const ang = Math.atan2(f.y, f.x);
      const len = T * 12;
      const bg = dctx.createRadialGradient(px, py, 0, px, py, len);
      bg.addColorStop(0, 'rgba(0,0,0,.9)');
      bg.addColorStop(1, 'rgba(0,0,0,0)');
      dctx.fillStyle = bg;
      dctx.beginPath();
      dctx.moveTo(px, py);
      dctx.arc(px, py, len, ang - 0.32, ang + 0.32);
      dctx.closePath();
      dctx.fill();
    }
    ctx.drawImage(dark, 0, 0, W, H);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const wg = ctx.createRadialGradient(px, py, 0, px, py, radius * 0.9);
    wg.addColorStop(0, powered ? 'rgba(255,71,13,.16)' : 'rgba(255,71,13,.10)');
    wg.addColorStop(1, 'rgba(255,71,13,0)');
    ctx.fillStyle = wg;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();

    for (const g of ghosts) drawGhostEyes(g);

    for (const pu of popups) {
      ctx.globalAlpha = Math.max(0, 1 - pu.t);
      ctx.fillStyle = C.ember;
      ctx.font = "15px CubittHwBrunson, 'Arial Narrow', sans-serif";
      ctx.textAlign = 'center';
      ctx.fillText(pu.txt, (pu.x + 0.5) * T, (pu.y + 0.5) * T - pu.t * 18);
      ctx.globalAlpha = 1;
    }

    if (state === 'ready' || state === 'paused' || state === 'clear' || state === 'prize') {
      const label =
        state === 'ready' ? t.ready : state === 'paused' ? t.paused : state === 'prize' ? t.allCaught : logoDone ? t.logoComplete : t.levelClear;
      ctx.font = "30px CubittHwBrunson, 'Arial Narrow', sans-serif";
      ctx.textAlign = 'center';
      ctx.fillStyle = C.lantern;
      ctx.shadowColor = C.lantern;
      ctx.shadowBlur = 14;
      ctx.fillText(String(label).toUpperCase(), W / 2, 11.72 * T);
      ctx.shadowBlur = 0;
    }
  }

  function drawPlayer() {
    const p = pos(player);
    const cx = (p.x + 0.5) * T;
    const cy = (p.y + 0.5) * T;
    const f = player.face;
    let r = T * 0.42;
    let rod = T * 0.62;
    if (state === 'dying') {
      const k = Math.max(0, 1 - stateT / 1.2);
      r *= k;
      rod *= k;
    }
    if (r <= 0.5) return;
    ctx.strokeStyle = C.lantern;
    ctx.lineWidth = T * 0.14;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx - f.x * r * 0.6, cy - f.y * r * 0.6);
    ctx.lineTo(cx - f.x * (r + rod), cy - f.y * (r + rod));
    ctx.stroke();
    const squash = player.moving ? 1 + Math.sin(time * 18) * 0.06 : 1;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(Math.atan2(f.y, f.x));
    ctx.scale(squash, 2 - squash);
    const g = ctx.createRadialGradient(-r * 0.3, -r * 0.35, 1, 0, 0, r);
    g.addColorStop(0, '#FF8F66');
    g.addColorStop(0.5, C.lantern);
    g.addColorStop(1, '#B83000');
    ctx.fillStyle = g;
    ctx.shadowColor = C.lantern;
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function ghostPath(cx, cy, r) {
    ctx.beginPath();
    ctx.arc(cx, cy - r * 0.1, r, Math.PI, 0);
    const bottom = cy + r * 0.85;
    const waves = 3;
    const w = (2 * r) / waves;
    const off = Math.sin(time * 10) * r * 0.12;
    ctx.lineTo(cx + r, bottom);
    for (let i = 0; i < waves; i++) {
      const x0 = cx + r - i * w;
      ctx.quadraticCurveTo(x0 - w * 0.5, bottom - r * 0.35 + off, x0 - w, bottom);
    }
    ctx.closePath();
  }
  function drawGhostBody(g) {
    if (g.state === 'eaten') return;
    const p = pos(g);
    const bob = g.state === 'house' ? Math.sin(time * 5 + g.release) * T * 0.12 : 0;
    let col = g.color;
    if (g.state === 'fright') col = frightT < 2 && Math.floor(time * 8) % 2 ? C.bone : C.scared;
    ctx.fillStyle = col;
    ghostPath((p.x + 0.5) * T, (p.y + 0.5) * T + bob, T * 0.44);
    ctx.fill();
  }
  function drawGhostEyes(g) {
    const p = pos(g);
    const bob = g.state === 'house' ? Math.sin(time * 5 + g.release) * T * 0.12 : 0;
    const cx = (p.x + 0.5) * T;
    const cy = (p.y + 0.5) * T + bob - T * 0.08;
    const d = g.dir || { x: 0, y: 0 };
    const fright = g.state === 'fright';
    for (const s of [-1, 1]) {
      const ex = cx + s * T * 0.16;
      if (fright) {
        ctx.fillStyle = frightT < 2 && Math.floor(time * 8) % 2 ? '#FF470D' : C.bone;
        ctx.beginPath();
        ctx.arc(ex, cy, T * 0.06, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = '#FF470D';
        ctx.shadowColor = 'rgba(255,120,70,.85)';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.ellipse(ex, cy, T * 0.1, T * 0.13, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#0C1014';
        ctx.beginPath();
        ctx.arc(ex + d.x * T * 0.05, cy + d.y * T * 0.06, T * 0.055, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    if (fright) {
      ctx.strokeStyle = ctx.fillStyle;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let i = 0; i <= 6; i++) {
        const x = cx - T * 0.2 + i * T * 0.066;
        const y = cy + T * 0.2 + (i % 2 ? -2 : 2);
        if (i) ctx.lineTo(x, y);
        else ctx.moveTo(x, y);
      }
      ctx.stroke();
    }
  }

  // ---------- bucle (solo mientras el juego está abierto) ----------
  function frame(now) {
    const dt = Math.min(0.033, (now - last) / 1000);
    last = now;
    time += dt;
    stateT += dt;
    if (state === 'ready' && stateT > 1.6) setState('playing');
    else if (state === 'playing') {
      updatePlayer(dt);
      if (state === 'playing') updateGhosts(dt);
    } else if (state === 'dying' && stateT > 1.6) {
      lives--;
      hud();
      if (lives <= 0) gameOver();
      else {
        resetActors();
        setState('ready');
      }
    } else if (state === 'prize' && stateT > 1.4) {
      pendingWin = false;
      showWin();
    } else if (state === 'clear' && stateT > (logoDone ? 3 : 1.8)) {
      logoDone = false;
      level++;
      resetPellets();
      resetActors();
      hud();
      if (pendingWin) {
        pendingWin = false;
        showWin();
      } else setState('ready');
    }
    for (const pu of popups) pu.t += dt * 1.2;
    popups = popups.filter((pu) => pu.t < 1);
    draw();
    raf = requestAnimationFrame(frame);
  }

  function gameOver() {
    setState('over');
    if (score > best) {
      best = score;
      try {
        localStorage.setItem('cubitt-hw-best', best);
      } catch (e) {}
    }
    hud();
    $('ovTitle').textContent = t.overTitle;
    const s = $('ovScore');
    s.hidden = false;
    s.textContent = score;
    $('ovText').textContent = t.overText.replace('[nivel]', level);
    $('start').textContent = t.again;
    overlay.hidden = false;
  }

  function showWin() {
    setState('won');
    winEl.hidden = false;
    tone(523, 0.3, 'triangle', 0.07, 1046);
    ($('copy') || $('keep')).focus({ preventScroll: true });
    mount.dispatchEvent(new CustomEvent('cubitt-halloween:win', { bubbles: true, detail: { level, score } }));
  }

  function start() {
    overlay.hidden = true;
    initAudio();
    newGame();
  }
  $('start').addEventListener('click', start);
  $('keep').addEventListener('click', () => {
    winEl.hidden = true;
    setState(state === 'won' && BY_PRODUCTS ? 'playing' : 'ready');
  });
  if ($('copy')) {
    $('copy').addEventListener('click', () => {
      const done = () => {
        $('copy').textContent = t.copied;
      };
      if (navigator.clipboard) navigator.clipboard.writeText(cfg.coupon).then(done, done);
      else done();
    });
  }

  function togglePause() {
    if (state === 'playing') setState('paused');
    else if (state === 'paused') setState('playing');
    $('pause').textContent = state === 'paused' ? t.resume : t.pause;
  }
  $('pause').addEventListener('click', togglePause);
  $('sound').addEventListener('click', (e) => {
    muted = !muted;
    e.currentTarget.textContent = muted ? t.soundOff : t.soundOn;
  });

  // ---------- controles ----------
  const KEYMAP = { ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down', ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right' };
  function key(e) {
    if (e.key === 'Escape') {
      requestClose();
      return;
    }
    if (KEYMAP[e.code]) {
      want = DIRS[KEYMAP[e.code]];
      e.preventDefault();
    } else if (e.code === 'Space' || e.code === 'KeyP') {
      if (!overlay.hidden || !winEl.hidden) return;
      togglePause();
      e.preventDefault();
    } else if (e.code === 'Enter' && !overlay.hidden && document.activeElement === document.body) {
      start();
    }
  }
  mount.querySelectorAll('.cubitt-hw-pad button').forEach((b) =>
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      want = DIRS[b.dataset.d];
    })
  );
  let touch0 = null;
  mount.addEventListener('pointerdown', (e) => {
    if (e.target.closest('button, a')) return;
    touch0 = { x: e.clientX, y: e.clientY };
  });
  mount.addEventListener('pointermove', (e) => {
    if (!touch0) return;
    const dx = e.clientX - touch0.x;
    const dy = e.clientY - touch0.y;
    if (Math.hypot(dx, dy) > 18) {
      want = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? DIRS.right : DIRS.left) : dy > 0 ? DIRS.down : DIRS.up;
      touch0 = { x: e.clientX, y: e.clientY };
    }
  });
  const clearTouch = () => {
    touch0 = null;
  };
  mount.addEventListener('pointerup', clearTouch);
  mount.addEventListener('pointercancel', clearTouch);
  if ($('swipe') && window.matchMedia('(pointer:coarse)').matches) $('swipe').hidden = false;

  // ---------- sonido ----------
  let ac = null;
  let blipHi = false;
  function initAudio() {
    try {
      ac = ac || new (window.AudioContext || window.webkitAudioContext)();
      ac.resume();
    } catch (e) {
      ac = null;
    }
  }
  function tone(f, dur, type = 'square', vol = 0.04, f2) {
    if (!ac || muted) return;
    const o = ac.createOscillator();
    const g = ac.createGain();
    const now = ac.currentTime;
    o.type = type;
    o.frequency.setValueAtTime(f, now);
    if (f2) o.frequency.exponentialRampToValueAtTime(f2, now + dur);
    g.gain.setValueAtTime(vol, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    o.connect(g).connect(ac.destination);
    o.start(now);
    o.stop(now + dur);
  }
  function blip() {
    blipHi = !blipHi;
    tone(blipHi ? 520 : 390, 0.06, 'square', 0.025);
  }

  // Tablero vivo detrás de la portada del juego.
  resetPellets();
  resetActors();
  hud();

  return {
    layoutName,
    key,
    fit,
    show() {
      fit();
      last = performance.now();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(frame);
      $('start').focus({ preventScroll: true });
    },
    hide() {
      if (state === 'playing') togglePause();
      cancelAnimationFrame(raf);
      if (ac) ac.suspend().catch(() => {});
    },
    resume() {
      if (ac) ac.resume().catch(() => {});
    },
  };
}

class CubittHalloweenGame extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const configEl = this.querySelector('[data-hw-config]');
    if (!configEl) return;
    this.cfg = JSON.parse(configEl.textContent);
    this.modal = this.querySelector('[data-hw-modal]');
    this.mountEl = this.querySelector('[data-hw-mount]');
    this.querySelectorAll('[data-hw-open]').forEach((el) =>
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this._byKeyboard = e.detail === 0;
        this.open();
      })
    );
    this.querySelector('[data-hw-close]').addEventListener('click', () => this.close());
    this._onKey = (e) => this.game && this.game.key(e);
    this._onResize = () => {
      if (!this.game) return;
      // Si se gira el teléfono, se cambia de tablero (vertical/horizontal).
      if (pickLayout() !== this.game.layoutName && !this._playing()) this.mount();
      this.game.fit();
    };

    // Cubitt: el popup de Halloween lleva aquí con ?hw=jugar desde otras páginas: se abre el juego y se limpia la URL.
    const params = new URLSearchParams(window.location.search);
    if (params.get('hw') === 'jugar') {
      params.delete('hw');
      const query = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
      window.requestAnimationFrame(() => this.open());
    }
  }

  _playing() {
    const overlay = this.mountEl.querySelector('[data-hw="overlay"]');
    return overlay && overlay.hidden;
  }

  mount() {
    if (this.game) this.game.hide();
    this.game = createGame(this.mountEl, this.cfg, () => this.close());
  }

  open() {
    this._lastFocus = document.activeElement;
    this.modal.hidden = false;
    document.documentElement.classList.add('cubitt-hw-lock');
    if (!this.game || pickLayout() !== this.game.layoutName) this.mount();
    // Las fuentes del diseño se piden solo al abrir el juego.
    if (document.fonts && document.fonts.load) {
      document.fonts.load("30px CubittHwBrunson").then(() => this.game && this.game.fit()).catch(() => {});
    }
    this.game.show();
    this.game.resume();
    document.addEventListener('keydown', this._onKey);
    window.addEventListener('resize', this._onResize);
  }

  close() {
    if (this.game) this.game.hide();
    this.modal.hidden = true;
    document.documentElement.classList.remove('cubitt-hw-lock');
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('resize', this._onResize);
    if (this._byKeyboard && this._lastFocus && this._lastFocus.focus) this._lastFocus.focus({ preventScroll: true });
    else if (document.activeElement && this.contains(document.activeElement)) document.activeElement.blur();
  }
}

if (!customElements.get('cubitt-halloween-game')) customElements.define('cubitt-halloween-game', CubittHalloweenGame);

